'use client';

import { Box, Button, Center, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { ArrowUpRight, FileText, FolderOpen, Palette, LayoutGrid, List } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { InlineSpinner } from '@/components/shared/PageLoader';
import type { CanvasArtifactSummary } from '@/lib/canvas/shared';
import { formatCodexRelativeTime } from '@/lib/codex-workspace-format';
import {
  collectDocsAndAssets,
  filterDocsAndAssets,
  type LibraryKind,
  type LibrarySort,
} from '@/lib/reports/docs-assets';
import type { ReportSummary } from '@/lib/reports/types';
import { FilterBar, FilterClearButton, FilterSearchInput } from '@/components/shared/FilterBar';
import { InlineFilterGroup } from '@/components/shared/InlineFilterControls';

export function DocsAssetsLibrary({
  reports,
  assets,
  loading,
  assetsLoading = false,
  assetError,
  onRetryAssets,
  onOpenReport,
  onOpenAsset,
  renderDocActions,
  headerActions,
  navigation,
}: {
  headerActions?: ReactNode;
  navigation?: ReactNode;
  reports: ReportSummary[];
  assets: CanvasArtifactSummary[];
  loading: boolean;
  assetsLoading?: boolean;
  assetError?: string | null;
  onRetryAssets: () => void;
  onOpenReport: (id: string) => void;
  onOpenAsset?: (asset: CanvasArtifactSummary) => void;
  renderDocActions?: (report: ReportSummary) => ReactNode;
}) {
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [kind, setKind] = useState<LibraryKind>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<LibrarySort>('updated');
  // Publish both sources together so a slower request cannot reorder visible cards.
  // Keep the last complete result while refreshing this project.
  const [items, setItems] = useState(() =>
    loading || assetsLoading ? [] : collectDocsAndAssets(reports, assets)
  );
  const [resolved, setResolved] = useState(!loading && !assetsLoading);
  useEffect(() => {
    if (loading || assetsLoading) return;
    setItems(collectDocsAndAssets(reports, assets));
    setResolved(true);
  }, [reports, assets, loading, assetsLoading]);
  const visible = useMemo(
    () => filterDocsAndAssets(items, { kind, query, sort }),
    [items, kind, query, sort]
  );
  const filtered = kind !== 'all' || Boolean(query.trim());
  const pending = (kind !== 'asset' && loading) || (kind !== 'doc' && assetsLoading);
  const filters: { kind: LibraryKind; label: string; count: number }[] = [
    { kind: 'all', label: 'All', count: items.length },
    { kind: 'doc', label: 'Docs', count: items.filter(item => item.kind === 'doc').length },
    { kind: 'asset', label: 'Assets', count: items.filter(item => item.kind === 'asset').length },
  ];

  return (
    <Box minW={0}>
      <Flex align="center" justify="space-between" gap="4" wrap="wrap" mb="5">
        <Text as="h1" fontSize="22px" fontWeight="500" letterSpacing="-0.03em">
          Library
        </Text>
        <Flex align="center" gap="2" wrap="wrap" w={{ base: 'full', md: 'auto' }}>
          <HStack gap="0" aria-label="Library view">
            <IconButton
              aria-label="Grid view"
              aria-pressed={view === 'grid'}
              variant="ghost"
              borderRadius="full"
              bg={view === 'grid' ? 'ui.surface.input' : undefined}
              onClick={() => setView('grid')}
            >
              <LayoutGrid size={17} />
            </IconButton>
            <IconButton
              aria-label="List view"
              aria-pressed={view === 'list'}
              variant="ghost"
              borderRadius="full"
              bg={view === 'list' ? 'ui.surface.input' : undefined}
              onClick={() => setView('list')}
            >
              <List size={17} />
            </IconButton>
          </HStack>
          {headerActions}
        </Flex>
      </Flex>
      <FilterBar mb="5" aria-label="Library filters">
        <FilterSearchInput
          aria-label="Search docs and assets"
          placeholder="Search library"
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
        <InlineFilterGroup
          label="Type"
          value={kind}
          onChange={value => setKind(value as LibraryKind)}
          options={filters.map(filter => ({
            value: filter.kind,
            label: filter.kind === 'all' ? 'All types' : filter.label,
            metaLabel:
              (filter.kind === 'doc' && loading) || (filter.kind === 'asset' && assetsLoading)
                ? '–'
                : filter.count,
          }))}
        />
        {navigation}
        <InlineFilterGroup
          label="Sort"
          value={sort}
          onChange={value => setSort(value as LibrarySort)}
          minSelectWidth="150px"
          options={[
            { value: 'updated', label: 'Recently updated' },
            { value: 'name', label: 'Name A–Z' },
          ]}
        />
        {query ? <FilterClearButton onClick={() => setQuery('')} /> : null}
      </FilterBar>
      <HStack h="10" mb="2" px="1" justify="space-between" color="ui.text.tertiary">
        <Text fontSize="xs" role="status" lineClamp={2}>
          {assetError
            ? 'Assets couldn’t load. Docs are still available.'
            : pending
              ? resolved
                ? 'Refreshing library…'
                : 'Loading library…'
              : `${visible.length} ${visible.length === 1 ? 'item' : 'items'}${filtered ? ` of ${items.length}` : ''}`}
        </Text>
        {assetError ? (
          <Button flexShrink={0} variant="ghost" size="xs" onClick={onRetryAssets}>
            Retry
          </Button>
        ) : null}
      </HStack>
      {!resolved ? (
        <Center minH="240px" aria-label="Loading docs and assets" aria-busy="true">
          <InlineSpinner size="md" decorative />
        </Center>
      ) : visible.length ? (
        <>
          <Box
            as="ul"
            display={view === 'grid' ? 'grid' : 'flex'}
            gridTemplateColumns="repeat(auto-fill, minmax(min(100%, 240px), 1fr))"
            flexDirection="column"
            aria-label="Docs and assets"
            alignItems="stretch"
            gap={view === 'grid' ? '3' : '0'}
            listStyleType="none"
            borderWidth={view === 'grid' ? '0' : '1px'}
            borderColor="ui.border.subtle"
            borderRadius="xl"
            overflow="hidden"
            bg={view === 'grid' ? 'transparent' : 'ui.surface.drawer'}
          >
            {visible.map(item => {
              const asset = item.kind === 'asset';
              const failed =
                item.value.status === 'build_failed' || item.value.status === 'update_failed';
              const pending =
                item.value.status === 'generating' || item.value.status === 'building';
              const label = failed ? 'Needs attention' : pending ? 'Creating' : 'Ready';
              const disabled = asset && !onOpenAsset;
              return (
                <HStack
                  as="li"
                  key={`${item.kind}:${item.value.id}`}
                  gap="1"
                  position="relative"
                  minW={0}
                  h={view === 'grid' ? '186px' : undefined}
                  flexDirection={view === 'grid' ? 'column' : 'row'}
                  align="stretch"
                  p="0"
                  pr={view === 'grid' ? '0' : '2'}
                  borderRadius={view === 'grid' ? 'xl' : '0'}
                  bg="ui.surface.drawer"
                  boxShadow="none"
                  borderWidth={view === 'grid' ? '1px' : '0'}
                  borderBottomWidth="1px"
                  borderColor="ui.border.subtle"
                  _last={{ borderBottomWidth: view === 'grid' ? '1px' : 0 }}
                  _hover={{ bg: 'ui.surface.rowHover' }}
                >
                  <Button
                    variant="plain"
                    flex="1"
                    minW={0}
                    h={view === 'grid' ? 'full' : 'auto'}
                    minH={view === 'grid' ? '184px' : '76px'}
                    flexDirection={view === 'grid' ? 'column' : 'row'}
                    alignItems={view === 'grid' ? 'stretch' : 'center'}
                    p={view === 'grid' ? '5' : '3.5'}
                    gap="3"
                    textAlign="left"
                    justifyContent="flex-start"
                    whiteSpace="normal"
                    disabled={disabled}
                    title={disabled ? 'Open assets on desktop' : `Open ${item.value.title}`}
                    onClick={() =>
                      item.kind === 'asset'
                        ? onOpenAsset?.(item.value)
                        : onOpenReport(item.value.id)
                    }
                  >
                    <Center
                      boxSize={view === 'grid' ? '32px' : '36px'}
                      alignSelf={view === 'grid' ? 'flex-start' : undefined}
                      order={view === 'grid' ? 0 : undefined}
                      flexShrink={0}
                      borderRadius="lg"
                      bg={view === 'grid' ? 'transparent' : 'ui.surface.input'}
                      color={asset ? 'accent.fg' : 'blue.500'}
                    >
                      {asset ? (
                        <Palette size={view === 'grid' ? 26 : 20} aria-hidden="true" />
                      ) : (
                        <FileText size={view === 'grid' ? 26 : 20} aria-hidden="true" />
                      )}
                    </Center>
                    <Box flex="1" minW={0} display={view === 'grid' ? 'contents' : 'block'}>
                      <Text
                        order={view === 'grid' ? 1 : undefined}
                        fontSize="sm"
                        lineHeight="1.5"
                        fontWeight="500"
                        color="ui.text.primary"
                        lineClamp={2}
                      >
                        {item.value.title}
                      </Text>
                      <Flex
                        order={view === 'grid' ? 2 : undefined}
                        mt={view === 'grid' ? 'auto' : '1'}
                        pr={view === 'grid' ? '64px' : '0'}
                        columnGap="2"
                        rowGap="1"
                        wrap="wrap"
                        align="center"
                        fontSize="xs"
                        color="ui.text.tertiary"
                        fontWeight="400"
                      >
                        <Text>
                          {item.kind === 'asset'
                            ? 'Asset'
                            : item.value.automationId
                              ? 'Scheduled doc'
                              : 'Doc'}
                        </Text>
                        {view === 'list' || failed || pending ? (
                          <Text aria-hidden="true">·</Text>
                        ) : null}
                        {view === 'list' || failed || pending ? (
                          <HStack
                            gap="1"
                            color={
                              failed
                                ? 'status.warning.fg'
                                : pending
                                  ? 'status.info.fg'
                                  : 'ui.text.secondary'
                            }
                          >
                            <Box boxSize="1.5" borderRadius="full" bg="currentColor" />
                            <Text>{label}</Text>
                          </HStack>
                        ) : null}
                        {view === 'list' && item.value.currentVersion > 0 ? (
                          <Text>· v{item.value.currentVersion}</Text>
                        ) : null}
                        <Text>· {formatCodexRelativeTime(item.value.updatedAt)}</Text>
                        {disabled ? <Text>· Open on desktop</Text> : null}
                      </Flex>
                    </Box>
                  </Button>
                  <HStack
                    justify="flex-end"
                    alignSelf="center"
                    position={view === 'grid' ? 'absolute' : undefined}
                    bottom={view === 'grid' ? '3' : undefined}
                    right={view === 'grid' ? '3' : undefined}
                    color="ui.text.tertiary"
                  >
                    {item.kind === 'doc' && renderDocActions ? (
                      renderDocActions(item.value)
                    ) : (
                      <Box color="ui.text.tertiary" pr="2" aria-hidden="true">
                        <ArrowUpRight size={16} />
                      </Box>
                    )}
                  </HStack>
                </HStack>
              );
            })}
          </Box>
        </>
      ) : (
        <Center
          minH="240px"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="ui.border.subtle"
          borderRadius="xl"
        >
          <VStack maxW="300px" p="6" gap="3" textAlign="center">
            <Center boxSize="48px" bg="ui.surface.input" borderRadius="xl" color="ui.text.tertiary">
              <FolderOpen size={23} />
            </Center>
            <Box>
              <Text fontWeight="600" fontSize="sm">
                {filtered ? 'No matching items' : 'Your work belongs here'}
              </Text>
              <Text mt="1" fontSize="sm" color="ui.text.secondary">
                {filtered
                  ? 'Try another name or change the type filter.'
                  : 'Create a doc or ask Attune to make an asset. Find them together here.'}
              </Text>
            </Box>
            {filtered ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setKind('all');
                  setQuery('');
                }}
              >
                Clear filters
              </Button>
            ) : null}
          </VStack>
        </Center>
      )}
    </Box>
  );
}
